/**
 * 분할 생성 그룹 선택 모달 — 결정 2026-09-01 「Composer 분할 생성」 §③-1·§③-2·§③-3.
 *
 * 도메인 지식은 0 — 여기 있는 것은 "그룹과 행을 고르게 하고 서버에 넘긴다"뿐이고, 무엇으로
 * 묶였는지(공종·장소·…)는 서버 provider가 안다.
 *
 * **계획은 제안이지 결정이 아니다**(§③-2). 사용자는 행을 끄고(부분 선택), 다른 그룹으로 옮기고,
 * 계획에 없는 새 그룹을 만들 수 있다. 서버는 그 배치를 재분류하지 않고 검증만 한다.
 *
 * 두 갈래로 끝난다:
 * - **개별(split)**: 고른 그룹마다 DRAFT 1건 — `POST /reports/composed/split` → 문서 목록으로.
 * - **통합(combined)**: 분할하지 않음 = 1건 — 종전 작성 페이지 진입 그대로(`onCombined`).
 *   여기에 combined 분기를 만들지 않는 이유가 결정 §③-1이다("통합은 기준이 아니라 모드").
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  // DOM 전역 `KeyboardEvent`(문서 리스너에서 쓴다)와 이름이 겹쳐 별칭으로 받는다.
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Modal, TriStateCheckbox } from '@safetyhub/ui';
import {
  SPLIT_CUSTOM_GROUP_KEY_PREFIX,
  SPLIT_UNCLASSIFIED_LABEL,
  normalizeSplitGroupKey,
  type SplitCreateGroupSelection,
  type SplitExistingReport,
  type SplitGroup,
  type SplitGroupRow,
  type SplitMode,
  type SplitPlan,
} from '@safetyhub/shared';
import { ChevronDownIcon, PlusIcon } from '../icons';
import { MODAL_LAYER, SUB_MODAL_OVERLAY_BG } from '../modalLayers';

/** 행 이동 팝오버 폭 — 클래스 `w-56`(14rem)의 픽셀값. 좌표 계산에 필요해 여기서도 갖는다. */
const MENU_WIDTH = 224;
/** 트리거와 팝오버 사이 간격(클래스 `mt-1` 자리). */
const MENU_GAP = 4;
/** 뷰포트 가장자리 여백 — 화면 끝에 딱 붙지 않게. */
const VIEWPORT_MARGIN = 8;
/** 아래 공간이 이보다 좁으면 위로 뒤집는다(대략 항목 3~4개 높이). */
const MENU_MIN_HEIGHT = 200;

/** 생성 방식 라디오 — 순서가 곧 화살표 이동 순서다. */
const MODE_OPTIONS: { value: SplitMode; label: string; hint: string }[] = [
  { value: 'split', label: '개별', hint: '그룹마다 문서를 따로 만듭니다.' },
  { value: 'combined', label: '통합', hint: '나누지 않고 1개의 문서로 만듭니다.' },
];

/** 사진별 행 묶음 — 렌더 단위. photoUrl 없는 행은 사진 없이 저마다 한 묶음이다. */
interface RowPhotoBundle {
  key: string;
  photoUrl?: string;
  rows: SplitGroupRow[];
}

/**
 * 그룹 안 행을 **사진별로 묶는다** — 구 TBM 공종별 그룹화(`pages/tbm/new.tsx` GroupCard의
 * photoGroups) 동형(2026-09-07 사용자 확정: 행별 40px 정사각은 식별하기에 너무 작았다).
 * 같은 사진의 발견사항 여러 건이 한 그룹에 있으면 사진은 **한 번만** 큼직하게 걸고 그 아래
 * 행들을 나열한다 — 행마다 같은 사진을 반복하면 크기를 키우는 순간 목록이 세로로 폭발한다.
 *
 * 묶음 순서는 첫 등장 순(구 photoGroups의 Map 방식 그대로) — 행 이동으로 같은 사진 행이
 * 비인접이 되면 첫 등장 자리에 합류한다. photoUrl 없는 행은 묶지 않고 원래 자리에 남긴다
 * (없는 사진끼리 "같은 묶음"인 것처럼 붙이면 무관한 행들이 한 덩어리로 보인다).
 */
function bundleRowsByPhoto(rows: SplitGroupRow[]): RowPhotoBundle[] {
  const bundles: RowPhotoBundle[] = [];
  const byPhoto = new Map<string, number>();
  for (const row of rows) {
    if (!row.photoUrl) {
      bundles.push({ key: `row-${row.rowId}`, rows: [row] });
      continue;
    }
    const idx = byPhoto.get(row.photoUrl);
    if (idx !== undefined) {
      bundles[idx].rows.push(row);
    } else {
      byPhoto.set(row.photoUrl, bundles.length);
      bundles.push({ key: `photo-${row.photoUrl}`, photoUrl: row.photoUrl, rows: [row] });
    }
  }
  return bundles;
}

export interface SplitGroupSelectModalProps {
  isOpen: boolean;
  /** 템플릿 표시명 — 제목·안내 문구에 쓴다. */
  templateName: string;
  plan: SplitPlan;
  /** 이미 만들어진 형제 문서(그룹당 최대 1건) — 해제·비활성·열기 링크의 근거. */
  existing: SplitExistingReport[];
  /** 생성 진행 중 — 닫기 수단 전부 차단 + 버튼 비활성. */
  isSubmitting?: boolean;
  onClose: () => void;
  /**
   * 개별(split) 확정 — 고른 그룹을 **작성자가 정한 배치 그대로** 넘긴다.
   * `rowIds`는 그 문서에 담길 행 전부다(체크가 꺼진 행은 아예 실리지 않는다).
   */
  onConfirmSplit: (groups: SplitCreateGroupSelection[]) => void;
  /** 통합(combined) 확정 — 종전 1문서 작성 흐름으로 보낸다. */
  onCombined: () => void;
  /**
   * 겹침 층 — **여는 쪽이 정한다**([[feedback-modal-layer-belongs-to-opener-not-component]]).
   * 미지정 시 `MODAL_LAYER.sub`(문서 유형 선택 모달 `z-[70]` 위).
   */
  layer?: string;
  /**
   * 기존 문서로 가는 링크를 걸지 않고 **표시만** 한다(기본 false = 링크 있음).
   *
   * 무인증 호스트(외부 위임 그룹 선택, §6)를 위한 것이다: `/reports/:id`는 인증 라우트라
   * 외부 담당자가 누르면 로그인 벽에 닿는다 — 해결 경로가 없는 막다른 길
   * ([[feedback-blocking-without-a-fix-path-creates-a-dead-end]]). 잠금·건너뜀 판정 자체는
   * `existing`이 그대로 하므로(고를 수 없다는 사실은 계속 보인다) 링크만 뗀다.
   */
  hideExistingLinks?: boolean;
  /**
   * 표면 — `'modal'`(기본)은 종전대로 겹쳐 띄우고, `'inline'`은 **호스트 페이지 본문에 그대로
   * 편다**(오버레이·포탈·Esc·포커스 트랩 없음).
   *
   * 인라인이 필요한 자리는 무인증 위임 그룹 선택 게이트다. 그 화면은 안내 한 카드 + 하단바
   * 버튼뿐이라 화면의 대부분이 비는데, 버튼을 누르면 그 위에 모달이 또 덮여 **한 흐름이 두
   * 겹**이 됐다(2026-09-10 사용자 지적). 계획 조회는 부수효과(AI 정제 캐시 getOrCreate +
   * TbmMeta upsert)를 가져 **명시적 조작 1회**로 묶어야 하므로 클릭 자체는 남기고, 도착한
   * 계획을 같은 화면에 펼쳐 겹을 하나로 줄인다.
   */
  variant?: 'modal' | 'inline';
}

// 접두는 shared 계약이 정본(`SPLIT_CUSTOM_GROUP_KEY_PREFIX`) — 이 re-export는 기존 소비처 호환.
export { SPLIT_CUSTOM_GROUP_KEY_PREFIX };

/**
 * 새 그룹 키 — **이름에서 결정적으로 파생**한다(계약: `SplitCreateGroupSelection.groupKey`).
 *
 * 구 위저드의 `custom-${Date.now()}`를 그대로 옮기면 안 되는 이유가 여기다: 키가 매번 달라지면
 * 중복 가드("같은 키 = 같은 그룹")를 통과해 같은 이름의 그룹을 몇 번이고 다시 만들 수 있다.
 * 정규화는 서버와 **같은 함수**(shared `normalizeSplitGroupKey`)라, 클라가 만든 키가 서버에서
 * 다시 바뀌지 않는다 — 화면이 보여 준 키와 박제되는 `report.splitGroupKey`가 갈리지 않게.
 */
export function deriveSplitGroupKey(name: string): string {
  return `${SPLIT_CUSTOM_GROUP_KEY_PREFIX}${normalizeSplitGroupKey(name)}`;
}

/** 그룹 표시명 — provider가 라벨을 못 낸 미분류는 공통 라벨로. */
function groupLabel(group: Pick<SplitGroup, 'groupKey' | 'label' | 'unclassified'>): string {
  if (group.unclassified && !group.label) return SPLIT_UNCLASSIFIED_LABEL;
  return group.label || group.groupKey;
}

/**
 * 그룹의 행 표시 데이터 — 서버 계획은 `rows`를 항상 채운다(§③-3). optional인 것은 옛 응답·
 * 픽스처 호환 때문이라, 없으면 id만으로 최소 표시를 만든다(집합은 `rowIds`와 같다).
 *
 * ⚠️ `rowIndexes`가 아니라 이쪽이 정본이다 — 앵커를 못 낸 행은 `rowIds`/`rows`에 없고 문서에
 * 담길 수도 없어서, 건수를 `rowIndexes`로 세면 담기지 않는 행까지 세게 된다.
 */
function planGroupRows(group: SplitGroup): SplitGroupRow[] {
  if (group.rows) return group.rows;
  return group.rowIds.map((rowId, i) => ({ rowId, label: `${i + 1}번째 행` }));
}

/**
 * 같은 내용(제목·본문·푸터)을 **모달로 덮거나 페이지 본문에 펴는** 표면 한 겹.
 *
 * 두 벌로 갈라 쓰지 않으려고 래퍼만 바꾼다 — 내용 JSX는 한 벌이라 모달·인라인이 갈릴 수 없다.
 * 인라인은 오버레이·포탈·Esc·포커스 트랩을 **의도적으로 갖지 않는다**: 페이지 본문 그 자체라
 * 가둘 초점도, 돌아갈 opener도 없다(그 규약은 겹쳐 뜨는 표면의 것이다).
 */
function SplitSurface({
  inline,
  title,
  footer,
  children,
  ...modalProps
}: React.ComponentProps<typeof Modal> & { inline?: boolean }) {
  if (!inline) {
    return (
      <Modal title={title} footer={footer} {...modalProps}>
        {children}
      </Modal>
    );
  }
  return (
    <section
      aria-label={typeof title === 'string' ? title : undefined}
      className="rounded-card border border-line bg-white shadow-card"
    >
      <div className="border-b border-line px-4 py-3 sm:px-6">
        <h2 className="text-base font-semibold text-ink-900">{title}</h2>
      </div>
      <div className="px-4 py-4 sm:px-6">{children}</div>
      <div className="border-t border-line px-4 py-3 sm:px-6">{footer}</div>
    </section>
  );
}

/** 모달이 들고 있는 **작성자의 배치** — 계획에서 출발해 이동·새 그룹으로 바뀐다. */
interface ArrangedGroup {
  groupKey: string;
  label: string;
  unclassified: boolean;
  /**
   * **그룹 통째 잠금** — 소유 행을 파생하지 못한 문서(`ownedRowIds === null`)가 그 키를 가진
   * 경우에만 참이다(옛 공종 축 폴백). 무엇을 담았는지 모르는 문서를 "아무것도 안 담았다"로
   * 읽으면 같은 내용의 문서가 둘 생기므로, 그때만 종전처럼 그룹을 통째로 닫는다.
   *
   * ⚠️ **문서가 있다 ≠ 잠김**(결정 2026-09-10). 공종 A의 5행 중 1행만 담은 문서가 있으면 그
   * 그룹은 **열려 있어야** 하고 남은 4행은 고르거나 옮길 수 있어야 한다 — 종전에는 그 4행이
   * 영영 갇혔다(사용자가 만난 막다른 길). 개별 행의 잠금은 `ownerByRowId`가 판정한다.
   */
  locked: boolean;
  rows: SplitGroupRow[];
}

export function SplitGroupSelectModal({
  isOpen,
  templateName,
  plan,
  existing,
  isSubmitting = false,
  onClose,
  onConfirmSplit,
  onCombined,
  layer,
  hideExistingLinks = false,
  variant = 'modal',
}: SplitGroupSelectModalProps) {
  /**
   * **이미 담긴 행 → 그 문서.** 잠금의 단위다(결정 2026-09-10 「행의 소유」).
   *
   * 서버 가드(`findSplitBlockingSibling`)와 같은 축이라, 여기서 못 고르는 것과 서버가 거부하는
   * 것이 갈리지 않는다 — 갈리면 고를 수는 있는데 저장은 안 되는 상태가 된다.
   */
  const ownerByRowId = useMemo(() => {
    const map = new Map<string, SplitExistingReport>();
    for (const doc of existing) {
      if (!doc.ownedRowIds) continue;
      for (const rowId of doc.ownedRowIds) {
        if (!map.has(rowId)) map.set(rowId, doc);
      }
    }
    return map;
  }, [existing]);

  /**
   * 소유 행을 **파생하지 못한** 문서(`ownedRowIds === null`)의 그룹 키 → 그 문서.
   * `null`은 "0건"이 아니라 "모른다"라, 그 그룹만 종전처럼 통째로 잠근다(보수적 폴백).
   */
  const opaqueGroups = useMemo(() => {
    const map = new Map<string, SplitExistingReport>();
    for (const doc of existing) {
      if (doc.ownedRowIds) continue;
      if (!map.has(doc.groupKey)) map.set(doc.groupKey, doc);
    }
    return map;
  }, [existing]);

  /**
   * 그룹 키별 기존 문서 — 헤더 배지용. **배열이다**: 행 축 전환 후 한 키에 문서가 둘일 수 있다
   * (공종 A의 1행을 담은 문서 + 남은 4행을 담은 문서). Map으로 접으면 하나가 안 보인다.
   */
  const docsByGroup = useMemo(() => {
    const map = new Map<string, SplitExistingReport[]>();
    for (const doc of existing) {
      const list = map.get(doc.groupKey);
      if (list) list.push(doc);
      else map.set(doc.groupKey, [doc]);
    }
    return map;
  }, [existing]);

  /** 이 행이 이미 어느 문서에 담겼나 — 그룹 통째 잠금(opaque)도 같은 답으로 접는다. */
  const rowOwner = useCallback(
    (groupKey: string, rowId: string): SplitExistingReport | undefined =>
      opaqueGroups.get(groupKey) ?? ownerByRowId.get(rowId),
    [opaqueGroups, ownerByRowId],
  );

  /**
   * **남은 행이 0건** — 만들 것이 없다(구 위저드 "모든 공종의 TBM이 이미 생성" 자리).
   *
   * ⚠️ 판정 축이 「모든 그룹에 문서가 있나」에서 「남은 행이 0인가」로 바뀌었다(결정 2026-09-10).
   * 그룹 축으로 세면 5행 중 1행만 담긴 공종이 "커버됨"이 되어 남은 4행이 갇힌다 — 사용자가
   * 실제로 만난 막다른 길이 그것이다.
   */
  const allCovered = useMemo(() => {
    let total = 0;
    for (const g of plan.groups) {
      for (const row of planGroupRows(g)) {
        total += 1;
        if (!rowOwner(g.groupKey, row.rowId)) return false;
      }
    }
    return total > 0;
  }, [plan, rowOwner]);

  const [mode, setMode] = useState<SplitMode>(plan.defaultMode);
  const modeRefs = useRef<Partial<Record<SplitMode, HTMLButtonElement | null>>>({});

  /**
   * radiogroup 키보드 — 화살표로 선택이 **이동과 동시에 바뀐다**(WAI-ARIA radio group의
   * 기본 규약). Home/End는 양 끝. 제출 중에는 선택 자체가 잠기므로 키도 받지 않는다.
   */
  const handleModeKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (isSubmitting) return;
    const current = MODE_OPTIONS.findIndex((o) => o.value === mode);
    const last = MODE_OPTIONS.length - 1;
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = current >= last ? 0 : current + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = current <= 0 ? last : current - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    const value = MODE_OPTIONS[next].value;
    setMode(value);
    modeRefs.current[value]?.focus();
  };

  const [groups, setGroups] = useState<ArrangedGroup[]>([]);
  /** 체크된 **행** id — 그룹 선택은 이 집합에서 파생한다(행 하나라도 켜져 있으면 선택). */
  const [checkedRows, setCheckedRows] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // 계획이 바뀌면(모달 재오픈·다른 템플릿) 배치와 기본 선택을 다시 세운다.
  // 기본값: **아직 담기지 않은** 행은 전부 선택, **미분류는 해제**(그룹이 아니라 "묶이지 않은
  // 나머지"라, 사용자가 의도적으로 켜야 문서가 생긴다). 구 위저드도 미분류를 별도 배지로 구분했다.
  useEffect(() => {
    setMode(plan.defaultMode);
    const arranged: ArrangedGroup[] = plan.groups.map((g) => ({
      groupKey: g.groupKey,
      label: groupLabel(g),
      unclassified: g.unclassified,
      // 그룹 통째 잠금은 opaque(소유 행 미파생)일 때뿐 — 문서가 있다는 사실만으로는 안 잠근다.
      locked: opaqueGroups.has(g.groupKey),
      rows: planGroupRows(g),
    }));
    setGroups(arranged);
    setCheckedRows(
      new Set(
        arranged
          .filter((g) => !g.locked && !g.unclassified)
          .flatMap((g) =>
            g.rows.filter((r) => !ownerByRowId.has(r.rowId)).map((r) => r.rowId),
          ),
      ),
    );
    // 통째 잠긴 그룹만 접어 둔다(고칠 것이 없다). 부분적으로 담긴 그룹은 **펼친다** — 남은 행을
    // 고르거나 옮기는 것이 이 화면에서 유일하게 그 행을 꺼내는 길이다.
    setExpanded(new Set(arranged.filter((g) => !g.locked).map((g) => g.groupKey)));
  }, [plan, opaqueGroups, ownerByRowId]);

  const toggleRow = (rowId: string) => {
    // 심층 방어 — UI 경로로는 `disabled`가 먼저 막는다. 이미 담긴 행은 켤 수 없다.
    if (ownerByRowId.has(rowId)) return;
    setCheckedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  /**
   * 그룹 체크박스 — 전체 켜기/끄기(부분 상태에서 누르면 전체 켜기로 통일).
   * **이미 담긴 행은 분모에서도 대상에서도 뺀다** — 안 그러면 "전체 선택"이 영원히 안 된다.
   */
  const setGroupChecked = (group: ArrangedGroup, next: boolean) => {
    if (group.locked) return; // 심층 방어 — UI 경로로는 `disabled`가 먼저 막는다
    setCheckedRows((prev) => {
      const set = new Set(prev);
      for (const r of group.rows) {
        if (ownerByRowId.has(r.rowId)) continue;
        if (next) set.add(r.rowId);
        else set.delete(r.rowId);
      }
      return set;
    });
  };

  const toggleExpanded = (groupKey: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  /**
   * 행을 다른 그룹으로 옮긴다(구 `new.tsx:394 moveFinding` 동형).
   * 빈 그룹은 목록에서 사라진다 — 구 동작(`.filter(g => g.findings.length > 0)`) 그대로.
   *
   * 옮긴 행은 **켠다**. 옮기는 행위 자체가 "이 행을 저 문서에 담겠다"는 의사표시라,
   * 옮겼는데 꺼져 있으면 사용자가 한 일이 아무 효과가 없다.
   */
  const moveRow = (rowId: string, fromGroupKey: string, toGroupKey: string) => {
    if (fromGroupKey === toGroupKey) return;
    // 이미 다른 문서에 담긴 행은 옮길 수 없다 — 그 행의 자리는 그 문서가 정했다.
    if (ownerByRowId.has(rowId)) return;
    setGroups((prev) => {
      const from = prev.find((g) => g.groupKey === fromGroupKey);
      const to = prev.find((g) => g.groupKey === toGroupKey);
      // 통째 잠긴(opaque) 그룹은 출발지도 도착지도 될 수 없다. **부분적으로 담긴 그룹은
      // 도착지가 될 수 있다** — 남은 행이 있으면 그 그룹으로 문서를 또 만들 수 있기 때문이다
      // (결정 2026-09-10: 잠기는 것은 담긴 행이지 공종이 아니다).
      if (!from || !to || from.locked || to.locked) return prev;
      const row = from.rows.find((r) => r.rowId === rowId);
      if (!row) return prev;
      return prev
        .map((g) => {
          if (g.groupKey === fromGroupKey) {
            return { ...g, rows: g.rows.filter((r) => r.rowId !== rowId) };
          }
          if (g.groupKey === toGroupKey) return { ...g, rows: [...g.rows, row] };
          return g;
        })
        .filter((g) => g.locked || g.rows.length > 0);
    });
    setCheckedRows((prev) => new Set(prev).add(rowId));
  };

  /**
   * 새 그룹을 만들어 행을 옮긴다(구 `new.tsx:433 createGroupAndMove` 동형).
   * 키는 이름 파생(`deriveSplitGroupKey`)이라, 같은 이름을 다시 쓰면 같은 그룹이 된다.
   *
   * 반환값은 실패 사유(없으면 `null`) — 호출부(팝오버)가 그 자리에 안내를 띄운다.
   */
  const createGroupAndMove = (
    name: string,
    rowId: string,
    fromGroupKey: string,
  ): string | null => {
    const trimmed = name.trim();
    if (!trimmed) return '그룹 이름을 입력하세요.';
    const newKey = deriveSplitGroupKey(trimmed);

    // 통째 잠긴(opaque) 키로는 만들어 봐야 서버가 건너뛴다 — 여기서 알린다.
    // ⚠️ **문서가 있다는 사실만으로는 막지 않는다**(결정 2026-09-10) — 그 문서가 담지 않은
    // 행으로 같은 이름의 그룹을 만드는 것은 정상이다(서버 가드도 행 축으로 판정한다).
    if (opaqueGroups.has(newKey)) return '이미 그 이름으로 만든 문서가 있습니다.';

    // 같은 이름의 그룹이 이미 목록에 있으면 새로 만들지 않고 그리로 옮긴다
    // (키가 이름 파생이라 "새 그룹"이 아니라 같은 그룹이다 — 요청 내 키 중복은 서버가 400).
    if (groups.some((g) => g.groupKey === newKey)) {
      moveRow(rowId, fromGroupKey, newKey);
      return null;
    }

    setGroups((prev) => {
      const from = prev.find((g) => g.groupKey === fromGroupKey);
      if (!from || from.locked) return prev;
      const row = from.rows.find((r) => r.rowId === rowId);
      if (!row) return prev;
      return [
        ...prev
          .map((g) =>
            g.groupKey === fromGroupKey
              ? { ...g, rows: g.rows.filter((r) => r.rowId !== rowId) }
              : g,
          )
          .filter((g) => g.locked || g.rows.length > 0),
        {
          groupKey: newKey,
          label: trimmed,
          unclassified: false,
          locked: false,
          rows: [row],
        },
      ];
    });
    setCheckedRows((prev) => new Set(prev).add(rowId));
    setExpanded((prev) => new Set(prev).add(newKey));
    return null;
  };

  /**
   * 만들어질 문서 = 통째 잠기지 않았고, **아직 담기지 않은** 켜진 행이 하나 이상인 그룹.
   * 이미 담긴 행은 실리지 않는다 — 실으면 서버 행 축 가드가 그 그룹을 통째로 skip한다.
   */
  const selections: SplitCreateGroupSelection[] = groups
    .filter((g) => !g.locked)
    .map((g) => ({
      groupKey: g.groupKey,
      label: g.label,
      rowIds: g.rows
        .filter((r) => !ownerByRowId.has(r.rowId) && checkedRows.has(r.rowId))
        .map((r) => r.rowId),
    }))
    .filter((g) => g.rowIds.length > 0);

  const selectedRowCount = selections.reduce((sum, g) => sum + g.rowIds.length, 0);

  const canSubmit =
    !isSubmitting && (mode === 'combined' || selections.length > 0);

  const handleConfirm = () => {
    if (!canSubmit) return;
    if (mode === 'combined') {
      onCombined();
      return;
    }
    onConfirmSplit(selections);
  };

  /**
   * 이동 대상 후보 — **통째 잠긴(opaque) 그룹만** 제외한다.
   *
   * ⚠️ 종전에는 "기존 문서가 있는 그룹"을 전부 뺐다(구 위저드 `!g.existingTbm`). 그 결과 부분적
   * 으로 담긴 공종이 후보에서 사라져 **갇힌 행이 나갈 길이 없었다** — 결정 2026-09-10이 「남은
   * 행이 있으면 그 공종은 이동 대상에도 남아야 한다」고 못박은 이유다.
   */
  const moveTargets = (fromGroupKey: string) =>
    groups.filter((g) => !g.locked && g.groupKey !== fromGroupKey);

  return (
    <SplitSurface
      inline={variant === 'inline'}
      open={isOpen}
      onClose={onClose}
      title={`${templateName} — 생성 범위 선택`}
      size="lg"
      closeOnOverlay={!isSubmitting}
      closeOnEsc={!isSubmitting}
      showCloseButton={!isSubmitting}
      closeLocked={isSubmitting}
      overlayZClassName={layer ?? MODAL_LAYER.sub}
      overlayBgClassName={SUB_MODAL_OVERLAY_BG}
      // 그룹·행 목록이 뷰포트를 넘칠 때 **본문만** 스크롤한다 — 헤더(✕)와 푸터(생성 건수 안내 ·
      // 취소 · 작성 시작)는 고정이다. 종전 값은 `panelClassName`으로 **패널**을 스크롤시켜 그
      // 버튼들과 「N개의 문서가 생성됩니다」 안내가 함께 밀렸다.
      // ⚠️ `inline` variant에서는 이 prop이 `SplitSurface`에서 **쓰이지 않는다**(페이지 본문 그
      // 자체라 가둘 뷰포트가 없다) — `modalProps`로 묶여 Modal 분기에만 전달된다.
      //
      // ⚠️ **클리핑 컨텍스트가 패널 → 본문으로 한 겹 안쪽으로 옮겨진다**(`overflow-y-auto`가
      // 새 클리핑 컨텍스트를 만든다) — 그래서 치환 전에 「본문 안 절대배치 자식이 본문 경계를
      // 넘을 수 있나」를 셌다: **하나이고 무관하다.** 그 하나가 행 이동 팝오버(`SplitRowMoveButton`
      // 의 `fixed z-30` 메뉴)이고, `fixed`는 컨테이닝 블록이 **뷰포트**라 조상 overflow가 몇 겹이든
      // 잘리지 않는다 — 그 전제(「조상에 transform이 없다」)를 치환 시점에 다시 확인했다: base
      // `Modal`에 `transform`·`filter`·`will-change`·`contain`이 **코드로는 0건**(히트는 JSDoc 한
      // 줄뿐)이고 `bodyScroll`이 붙이는 세 클래스에도 없다 ⇒ **치환이 그 전제를 깨지 않는다.**
      // ⚠️ 이 축은 **tsc·spec·프로브 어느 것도 못 본다**(jsdom에 레이아웃이 없다). 그 팝오버를
      // `absolute`로 되돌리면 그때는 **본문**이 자르니, 위 `updateMenuPosition` JSDoc을 함께 읽어라.
      bodyScroll
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-ink-500">
            {allCovered
              ? null
              : mode === 'combined'
                ? '1개의 문서가 생성됩니다.'
                : `${selections.length}개의 문서가 생성됩니다. (항목 ${selectedRowCount}건)`}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
              취소
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!canSubmit || allCovered}
            >
              {isSubmitting ? '생성 중…' : mode === 'combined' ? '작성 시작' : '초안 만들기'}
            </Button>
          </div>
        </div>
      }
    >
      {allCovered ? (
        <div className="py-6 text-center" data-testid="split-all-covered">
          <p className="text-sm text-ink-700">
            이 점검의 모든 항목으로 이미 {templateName} 문서가 생성되었습니다.
          </p>
          <p className="mt-1 text-xs text-ink-500">
            아래 목록에서 기존 문서를 열어 이어서 작성할 수 있습니다.
          </p>
          {/* 목록의 축은 **기존 문서**다(계획 그룹이 아니라). 행 축 전환 후 한 그룹에 문서가
              둘일 수도, 계획에 없는 커스텀 그룹 문서가 있을 수도 있어 계획을 훑으면 어긋난다. */}
          <ul className="mt-4 space-y-1 text-left">
            {existing.map((doc) => (
              <li key={doc.reportId} className="text-sm">
                <span className="text-ink-700">{doc.groupKey}</span>
                {hideExistingLinks ? (
                  <span className="ml-2 text-ink-500">{doc.title || '생성됨'}</span>
                ) : (
                  <Link
                    to={`/reports/${doc.reportId}`}
                    className="ml-2 text-brand-600 hover:underline"
                  >
                    {doc.title || '문서 열기'} &rarr;
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-4">
          {/* 개별/통합 모드 — 저작이 아니라 진입에서 고른다(결정 §③-1).
              `role="radiogroup"`을 선언했으면 키보드도 라디오처럼 굴어야 한다: Tab은 그룹을
              통째로 하나의 정지점으로 지나가고(roving tabindex — 선택된 것만 0), 그룹 안 이동은
              화살표가 맡는다. 선언만 하고 Tab으로 개별 순회하게 두면 보조기술이 읽는 구조와
              실제 조작이 어긋난다. */}
          <div
            className="flex gap-2"
            role="radiogroup"
            aria-label="생성 방식"
            onKeyDown={handleModeKeyDown}
          >
            {MODE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                ref={(el) => {
                  modeRefs.current[option.value] = el;
                }}
                aria-checked={mode === option.value}
                tabIndex={mode === option.value ? 0 : -1}
                onClick={() => setMode(option.value)}
                disabled={isSubmitting}
                className={`flex-1 rounded-card border-2 p-3 text-left transition-colors disabled:opacity-60 ${
                  mode === option.value
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-line hover:border-teal-300'
                }`}
              >
                <div className="text-sm font-medium text-ink-900">{option.label}</div>
                <div className="text-xs text-ink-500">{option.hint}</div>
              </button>
            ))}
          </div>

          {/* 서버가 통합을 제안한 이유 — **화면이 말하지 않으면 아무도 알 수 없다.**
              「미분류 (2건)」만 보이면 작성자는 그것이 *분류에 실패한 것*인지 *원래 그런 것*인지
              구분할 수 없고, 고칠 수 있는 원인(현장 업종 미지정)이 있어도 알아채지 못한다.

              두 조건을 **모두** 요구한다:
              - `plan.defaultMode === 'combined'` — 이 문구가 답하는 질문은 "왜 **못** 나누는가"다.
                split일 때의 사유는 서버 내부 어휘(구 TBM 기본값 등)라 사용자에게 보여 줄 것이 없다.
                ⚠️ 상태 `mode`가 아니라 **계획**을 본다 — 사용자가 개별로 뒤집었을 때가 오히려 이
                안내가 가장 필요한 순간이다(그래 봐야 문서 1건이 나온다는 뜻이므로).
              - 사유가 있다 — 없으면(옛 응답·픽스처) 자리 자체를 만들지 않는다. 빈 박스는 안내가 아니다. */}
          {plan.defaultMode === 'combined' && plan.defaultModeReason && (
            <p
              className="rounded-card bg-surface-soft p-3 text-sm text-ink-600"
              data-testid="split-default-mode-reason"
            >
              {plan.defaultModeReason}
            </p>
          )}

          {mode === 'combined' ? (
            <p
              className="rounded-card bg-surface-soft p-3 text-sm text-ink-600"
              data-testid="split-combined-notice"
            >
              1개의 문서가 생성됩니다. 전체 {plan.totalRows}건의 항목이 한 문서에 담깁니다.
            </p>
          ) : (
            <div className="space-y-2">
              {groups.map((group) => {
                const docs = docsByGroup.get(group.groupKey) ?? [];
                // ⚠️ tri-state의 **분모는 아직 담기지 않은 행**이다 — 이미 담긴 행을 세면
                // "전체 선택"이 영원히 채워지지 않는다(결정 2026-09-10 행 축).
                const selectableRows = group.locked
                  ? []
                  : group.rows.filter((r) => !ownerByRowId.has(r.rowId));
                const takenCount = group.rows.length - selectableRows.length;
                const checkedCount = selectableRows.filter((r) =>
                  checkedRows.has(r.rowId),
                ).length;
                const allChecked =
                  selectableRows.length > 0 && checkedCount === selectableRows.length;
                const someChecked =
                  checkedCount > 0 && checkedCount < selectableRows.length;
                const isOpen = expanded.has(group.groupKey);
                return (
                  <div
                    key={group.groupKey}
                    data-testid={`split-group-${group.groupKey}`}
                    className={`rounded-card border ${
                      group.locked ? 'border-line bg-surface-soft opacity-60' : 'border-line'
                    }`}
                  >
                    {/* 구 위저드 그룹 헤더와 같은 구조: 체크박스 + 별도 클릭 영역(펼침).
                        `<label>`로 감싸지 않는 이유는 그쪽이 "행 어디를 눌러도 토글"을 브라우저에
                        맡기게 되는데, 잠금 그룹에서 그 전달 경로가 가드보다 앞서기 때문이다. */}
                    <div className="flex items-center gap-3 p-3">
                      <TriStateCheckbox
                        checked={allChecked}
                        mixed={someChecked}
                        // 실제 방어선은 이 `disabled`다(spec이 재는 것도 여기까지 — 프로브 확인).
                        // `setGroupChecked`/`moveRow`의 잠금 가드는 이 렌더가 사라질 때를 위한
                        // 심층 방어이고, UI 경로로는 증명할 수 없다(자세한 이유는 spec의 주석).
                        disabled={group.locked || selectableRows.length === 0 || isSubmitting}
                        onChange={(next) => setGroupChecked(group, next)}
                        ariaLabel={group.label}
                        className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                      />
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(group.groupKey)}
                          aria-expanded={isOpen}
                          className="flex items-center gap-1 text-left text-sm font-medium text-ink-900"
                        >
                          {group.label}
                          {/* 「담김」을 별도로 말한다 — 「5건 중 1건 선택」만 보이면 나머지 4건을
                              *내가 안 고른 것*으로 읽는다. 잠긴 것과 안 고른 것은 다른 사실이다. */}
                          <span className="text-xs font-normal text-ink-500">
                            ({group.rows.length}건
                            {takenCount > 0 ? ` · ${takenCount}건 담김` : ''}
                            {!group.locked && checkedCount < selectableRows.length
                              ? ` · ${checkedCount}건 선택`
                              : ''}
                            )
                          </span>
                          <ChevronDownIcon
                            className={`h-4 w-4 text-ink-400 transition-transform ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {group.unclassified && (
                          <Badge tone="slate" shape="rounded">
                            미분류
                          </Badge>
                        )}
                        {/* 라벨은 서버 파생(`SplitExistingReport.statusLabel`)을 그대로 —
                            enum 직접 파생은 정본(`resolveReportStatusLabel`)의 무결재 완료
                            판정('완료' vs '결재완료')을 우회한다.
                            ⚠️ **배열이다** — 행 축 전환 후 한 그룹 키에 문서가 둘일 수 있다. */}
                        {docs.map((doc) =>
                          hideExistingLinks ? (
                            <span
                              key={doc.reportId}
                              className="inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800"
                            >
                              생성됨 · {doc.statusLabel}
                            </span>
                          ) : (
                            <Link
                              key={doc.reportId}
                              to={`/reports/${doc.reportId}`}
                              className="inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800 hover:bg-green-200"
                            >
                              생성됨 · {doc.statusLabel} &rarr;
                            </Link>
                          ),
                        )}
                      </div>
                    </div>

                    {isOpen && (
                      <div className="space-y-3 border-t border-line-soft px-3 py-2">
                        {/* 사진별 묶음(구 GroupCard 동형) — 사진 1장(구 AnnotatedThumbnail 크기:
                            모바일 전폭 h-40 / 데스크톱 160×128) 아래 그 사진의 행들을 나열한다.
                            사진은 서버가 정책 선언(rowPhoto)으로 채운 행만 갖는다 — 없으면 자리
                            자체를 만들지 않는다(빈 사각형은 안내가 아니다). alt는 비운다: 식별
                            정보는 이미 체크박스 aria-label(행 라벨)이 갖고 있어 중복 낭독만 된다.
                            testid는 묶음 첫 행 id — 행→사진 대응을 spec이 그 축으로 잰다. */}
                        {bundleRowsByPhoto(group.rows).map((bundle) => (
                          <div key={bundle.key}>
                            {bundle.photoUrl && (
                              <img
                                src={bundle.photoUrl}
                                alt=""
                                loading="lazy"
                                data-testid={`split-row-photo-${bundle.rows[0].rowId}`}
                                className="mb-2 h-40 w-full rounded-card border border-line object-cover sm:h-32 sm:w-40"
                              />
                            )}
                            <div className="space-y-1">
                              {bundle.rows.map((row) => {
                                // 이미 담긴 행 — **숨기지 않고 비활성으로 보여 준다**. 없어지면
                                // 사용자는 그 행이 사라진 줄 알고, 어디로 갔는지 물을 데가 없다.
                                const owner = rowOwner(group.groupKey, row.rowId);
                                return (
                                  <div
                                    key={row.rowId}
                                    data-testid={`split-row-${row.rowId}`}
                                    className={`flex items-start gap-2 ${owner ? 'opacity-60' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!owner && checkedRows.has(row.rowId)}
                                      disabled={!!owner || isSubmitting}
                                      onChange={() => toggleRow(row.rowId)}
                                      aria-label={row.label}
                                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-ink-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm text-ink-900">{row.label}</p>
                                      {row.sublabel && (
                                        <p className="text-xs text-ink-500">{row.sublabel}</p>
                                      )}
                                      {/* 어느 문서에 담겼는지 적는다 — 제목·상태 라벨은 계약에
                                          이미 실려 온다(`SplitExistingReport`). */}
                                      {owner && (
                                        <p
                                          className="text-xs text-ink-500"
                                          data-testid={`split-row-owner-${row.rowId}`}
                                        >
                                          {hideExistingLinks ? (
                                            <span>
                                              {owner.title || '기존 문서'}에 담김 ·{' '}
                                              {owner.statusLabel}
                                            </span>
                                          ) : (
                                            <Link
                                              to={`/reports/${owner.reportId}`}
                                              className="text-brand-600 hover:underline"
                                            >
                                              {owner.title || '기존 문서'}에 담김 ·{' '}
                                              {owner.statusLabel} &rarr;
                                            </Link>
                                          )}
                                        </p>
                                      )}
                                    </div>
                                    {!owner && (
                                      <SplitRowMoveButton
                                        rowLabel={row.label}
                                        targets={moveTargets(group.groupKey)}
                                        disabled={isSubmitting}
                                        onMove={(toGroupKey) =>
                                          moveRow(row.rowId, group.groupKey, toGroupKey)
                                        }
                                        onCreateGroup={(name) =>
                                          createGroupAndMove(name, row.rowId, group.groupKey)
                                        }
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </SplitSurface>
  );
}

/**
 * 행 이동 팝오버 — 구 위저드 `FindingMoveButton`(new.tsx:2367) 동형.
 * 대상 그룹 목록 + "새 그룹 만들기"(이름 입력 → 그 행을 옮김).
 */
function SplitRowMoveButton({
  rowLabel,
  targets,
  disabled,
  onMove,
  onCreateGroup,
}: {
  rowLabel: string;
  targets: ArrangedGroup[];
  disabled?: boolean;
  onMove: (toGroupKey: string) => void;
  /** 실패 사유를 돌려주면(중복 이름 등) 팝오버 안에 그대로 띄운다. */
  onCreateGroup: (name: string) => string | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties | null>(null);

  /**
   * 팝오버 좌표 — 트리거의 뷰포트 좌표에서 계산한다.
   *
   * `absolute`였을 때는 모달의 **스크롤 조상**이 잘라서, 아래쪽 행에서 메뉴를 열면 화면에 안
   * 보이는 자리로 밀려났다. `fixed`는 컨테이닝 블록이 뷰포트라 조상의 overflow에 잘리지 않는다
   * (포탈이 필요 없는 이유 — 조상에 transform이 없다. 생기면 이 전제가 깨지니 그때는 포탈로
   * 옮길 것). DOM 위치를 그대로 두므로 바깥 클릭 판정(`ref.contains`)과 모달 오버레이 안의
   * z 순서도 그대로다.
   *
   * ⚠️ 그 조상을 **「패널」로 적지 않는다.** 2026-10-26에 스크롤이 패널(`panelClassName=
   * "max-h-[90vh] overflow-y-auto"`)에서 base `Modal`의 `bodyScroll`(본문 div)로 옮겨졌다 —
   * 자르는 자리가 한 겹 안으로 들어왔을 뿐이고 **이 `fixed`가 필요한 이유는 그대로다**.
   * 되돌릴 조건은 「패널이 다시 스크롤한다」가 아니라 **「조상에 스크롤 컨테이너가 없어진다」**다.
   */
  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN),
    );
    const below = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN;
    const above = rect.top - MENU_GAP - VIEWPORT_MARGIN;
    // 아래가 좁고 위가 더 넓을 때만 뒤집는다. 어느 쪽이든 남은 높이를 maxHeight로 잘라
    // 목록이 길어도 뷰포트를 넘지 않게 한다(메뉴 안에서 스크롤).
    setMenuStyle(
      below < MENU_MIN_HEIGHT && above > below
        ? { left, bottom: window.innerHeight - rect.top + MENU_GAP, maxHeight: above }
        : { left, top: rect.bottom + MENU_GAP, maxHeight: below },
    );
  }, []);

  // 열려 있는 동안 스크롤·리사이즈를 따라간다. 스크롤은 capture로 받는다 —
  // 실제로 움직이는 것은 **모달 본문 div**(base `bodyScroll`)이고, 그 스크롤은 window까지
  // 버블하지 않는다. capture라 어느 요소가 스크롤 주체인지와 무관하게 잡히므로, 그 자리가
  // 패널에서 본문으로 옮겨진 2026-10-26 치환에도 이 배선은 그대로다.
  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  // 외부 클릭 시 닫기(구 위저드 동형 — mousedown/touchstart 양쪽).
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
        setIsCreatingNew(false);
        setNewGroupName('');
        setCreateError(null);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [isOpen]);

  const close = () => {
    setIsOpen(false);
    setIsCreatingNew(false);
    setNewGroupName('');
    setCreateError(null);
  };

  const handleCreateNew = () => {
    const error = onCreateGroup(newGroupName);
    if (error) {
      setCreateError(error);
      return;
    }
    close();
  };

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => {
          if (isOpen) {
            close();
            return;
          }
          // 첫 페인트부터 제자리에 있도록 열기 전에 좌표를 잡는다(effect는 페인트 뒤라 한 프레임 튄다).
          updateMenuPosition();
          setIsOpen(true);
        }}
        aria-label={`${rowLabel} — 다른 그룹으로 이동`}
        className="rounded-card-sm p-1.5 text-ink-400 transition-colors hover:bg-brand-50 hover:text-brand-600 disabled:opacity-50"
        title="다른 그룹으로 이동"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          data-testid="split-row-move-menu"
          style={menuStyle ?? undefined}
          className="fixed z-30 w-56 overflow-y-auto rounded-card border border-line bg-white py-1 shadow-card"
        >
          {targets.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[10px] font-medium uppercase text-ink-400">
                그룹으로 이동
              </div>
              {targets.map((g) => (
                <button
                  key={g.groupKey}
                  type="button"
                  onClick={() => {
                    onMove(g.groupKey);
                    close();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink-700 hover:bg-surface-hover"
                >
                  <span className="truncate">{g.label}</span>
                  <span className="shrink-0 text-xs text-ink-400">({g.rows.length}건)</span>
                </button>
              ))}
              <div className="my-1 border-t border-line-soft" />
            </>
          )}

          {!isCreatingNew ? (
            <button
              type="button"
              onClick={() => setIsCreatingNew(true)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-brand-600 hover:bg-brand-50"
            >
              <PlusIcon className="h-4 w-4" />
              새 그룹 만들기
            </button>
          ) : (
            <div className="space-y-2 px-3 py-2">
              <input
                type="text"
                autoFocus
                value={newGroupName}
                onChange={(e) => {
                  setNewGroupName(e.target.value);
                  setCreateError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateNew();
                  if (e.key === 'Escape') {
                    setIsCreatingNew(false);
                    setNewGroupName('');
                    setCreateError(null);
                  }
                }}
                aria-label="새 그룹 이름"
                placeholder="그룹명 입력"
                className="w-full rounded-control border border-line px-2.5 py-1.5 text-sm focus:border-brand-500 focus:ring-brand-500"
              />
              {createError && (
                <p role="alert" className="text-xs text-red-600">
                  {createError}
                </p>
              )}
              <div className="flex gap-1.5">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="flex-1"
                  onClick={handleCreateNew}
                  disabled={!newGroupName.trim()}
                >
                  확인
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setIsCreatingNew(false);
                    setNewGroupName('');
                    setCreateError(null);
                  }}
                >
                  취소
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SplitGroupSelectModal;
